<template>
  <div :class="activityIntroClasses">
    <div
      :aria-label="ariaLabelSlashImage"
      :class="backgroundSideClasses"
      role="figure"
      tabindex="0"
    >
      <div class="background-image" :style="backgroundImageStyles">
        <p
          v-if="titleScreen"
          :aria-label="titleScreen"
          aria-level="1"
          class="title-screen"
          role="heading"
          tabindex="0"
        >
          {{ titleScreen }}
        </p>
      </div>
    </div>
    <div :class="introductionSideClasses">
      <div class="introduction-content">
        <div class="container-intro-action-btns">
          <voice-over
            v-if="srcVoiceOver"
            :bg-color-hover="btnPlayBgColorHover"
            :size="sizeBtnPlay"
            :color-icon="btnPlayColorIcon"
            :bg-color="btnPlayBgColor"
            :btn-outline-color="btnPlayOutlineColor"
            :src="srcVoiceOver"
          ></voice-over>
          <text-to-speech v-else>
            <text-to-speech-button-play
              :bgColorHover="btnPlayBgColorHover"
              :size="sizeBtnPlay"
              :color-icon="btnPlayColorIcon"
              :bg-color="btnPlayBgColor"
              :btn-outline-color="btnPlayOutlineColor"
            />
            <text-to-speech-message
              :show="false"
              :message="`${textTitle}. ${message}`"
            ></text-to-speech-message>
          </text-to-speech>
          <div class="container-zoom-btn">
            <slot name="introduction-actions"></slot>
          </div>
        </div>
        <h2
          class="introduction-content__title"
          role="heading"
          aria-level="2"
          :aria-label="textTitle"
          tabindex="0"
        >
          {{ textTitle }}
        </h2>
        <div
          class="introduction-content__message text-description"
          :aria-label="message"
          tabindex="0"
        >
          <p v-html="message"></p>
        </div>
      </div>
      <div class="introduction-action">
        <d-e-button
          :aria-label="btnText"
          :btn-text="btnText"
          :btn-variant-hover="btnBgColorHover"
          :btn-outline-color="btnOutlineColor"
          @btnCallToAction="onNextView"
        ></d-e-button>
      </div>
    </div>
    <slot></slot>
  </div>
</template>
<script>
import smoothscroll from 'smoothscroll-polyfill';
import { DEButton } from '../../Buttons';
import {
  TextToSpeech,
  TextToSpeechButtonPlay,
  TextToSpeechMessage,
} from '../../TextToSpeech';
import VoiceOver from '../../VoiceOver/VoiceOver';

smoothscroll.polyfill();

export default {
  name: 'ActivityIntro',
  components: {
    VoiceOver,
    TextToSpeechMessage,
    TextToSpeechButtonPlay,
    TextToSpeech,
    DEButton,
  },
  props: {
    nextComponent: {
      type: String,
      required: false,
    },
    bgColorBackgroundSide: {
      type: String,
      required: false,
      default: null,
    },
    bgColorIntroductionSide: {
      type: String,
      required: false,
      default: null,
    },
    btnPlayBgColorHover: {
      type: String,
      required: false,
    },
    sizeBtnPlay: {
      type: [String, Number],
      default: '1em',
    },
    btnBgColorHover: {
      type: String,
      required: false,
    },
    btnPlayBgColor: {
      type: String,
      required: false,
      default: null,
    },
    btnPlayColorIcon: {
      type: String,
      required: false,
      default: null,
    },
    textColor: {
      type: String,
      required: false,
      default: null,
    },
    textTitle: {
      type: String,
      required: false,
      default: 'Introduction',
    },
    btnText: {
      type: String,
    },
    message: {
      type: String,
      required: true,
    },
    titleScreen: {
      type: String,
      required: false,
    },
    backgroundImageId: {
      type: String,
      required: false,
    },
    startAudioId: {
      type: String,
      default: 'start',
    },
    btnOutlineColor: {
      type: String,
      default: 'start',
    },
    btnPlayOutlineColor: {
      type: String,
      default: 'start',
    },
    ariaLabelSlashImage: {
      type: String,
      default: null,
    },
    srcVoiceOver: {
      type: String,
      default: null,
    },
  },
  mounted() {
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });

    this.enteredInteractive();
  },
  computed: {
    activityIntroClasses() {
      const classes = {
        'activity-intro': true,
      };

      classes[`text-${this.textColor}`] = !!this.textColor;

      return classes;
    },
    backgroundSideClasses() {
      const classes = {
        'activity-intro__background-side': true,
      };
      classes[`bg-${this.bgColorBackgroundSide}`] =
        !!this.bgColorBackgroundSide;

      return classes;
    },
    introductionSideClasses() {
      const classes = {
        'activity-intro__intro-side': true,
      };

      classes[`bg-${this.bgColorIntroductionSide}`] =
        !!this.bgColorIntroductionSide;

      return classes;
    },
    backgroundImageStyles() {
      const stylesBackgroundImage = {};

      if (this.backgroundImageId) {
        stylesBackgroundImage.backgroundImage = `url(${this.getImageById(
          this.backgroundImageId,
        )})`;
      }

      return {
        ...stylesBackgroundImage,
      };
    },
  },
  methods: {
    onNextView() {
      if (this.hasAudioById(this.startAudioId)) {
        this.getAudioById(this.startAudioId).play();
      }

      if (this.nextComponent) {
        this.onSwapComponent(this.nextComponent);
      }

      this.$emit('onNextView', this.nextComponent);
    },
  },
};
</script>

<style lang="stylus" scoped>
@import "~@discoveryeducation/theme/src/mixins.styl"

.activity-intro
  flexbox(flex)
  justify-content(space-between)
  min-height 100%
  height 100%

  &__background-side
    width 66.66%

    .background-image
      position relative
      background-repeat no-repeat
      width 100%
      height 100%

      .title-screen
        background: rgba(0, 0, 0, 0.502) 0% 0%;
        text-shadow: 0 2px 5px #000000CC;
        text-align: center;
        position: absolute;
        bottom: 20px;
        font-size: 24px;
        padding: 0.5em;
        letter-spacing: 0.6px;
        font-family: Nunito-Regular;
        left: 50%;
        margin: 0 auto;
        transform: translateX(-50%)

  &__intro-side
    width 33.34%
    padding 7em 4em 8em 4em
    flexbox(flex)
    flex-direction(column);
    justify-content(space-between);

.introduction-content
  &__title
    font-family 'Montserrat-Semibold'
    font-size 2.4em
    font-weight 600
    margin 0.83em 0 0.625em 0

  &__message
    font-size 1.7em

.container-intro-action-btns
  position relative
  flexbox(flex)
  align-items(center)

  .container-zoom-btn
    position relative
    margin-left 15px
    flexbox(flex)
    justify-content(center)
    align-items(center)

    /deep/ .zoom-value
      font-family 'Nunito-Regular'

    /deep/ .zoom-reset
      font-family 'Nunito-Regular'

.introduction-action
  flexbox(flex)
  justify-content(end);
  margin-top 1.5em

  button
    text-transform uppercase
</style>
